import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Iconography,
  Button,
  Loader,
  LoaderSizes,
  IconographySizes,
  IconographyThemes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { partnerEngagementConfigurationSliceAction } from "../../../../../../redux/slices/partner-program/engagement-configuration";
import { EngagementCategoryPayloadType } from "../../../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import "./AddCategoryModal.scss";

type Props = {
  open?: boolean;
  mode?: "add" | "edit";
  onClose: () => void;
  onSubmit: (payload: EngagementCategoryPayloadType) => void;
  initialValue?: Partial<EngagementCategoryPayloadType>;
};

const AddEditEngagementCategoryModal: React.FC<Props> = ({
  open = true,
  mode = "add",
  onClose,
  onSubmit,
  initialValue,
}) => {
  const dispatch = useAppDispatch();
  const {
    status: {
      addEngagementCategoryLoading,
      addEngagementCategoryStatus,
      addEngagementCategoryMessage,
      updateEngagementCategoryLoading,
      updateEngagementCategoryStatus,
      updateEngagementCategoryMessage,
    },
  } = useAppSelector((state) => state.partner_engagementConfiguration);

  const [form, setForm] = React.useState<EngagementCategoryPayloadType>({
    ID: initialValue?.ID,
    category_name: initialValue?.category_name ?? "",
  });
  const [errors, setErrors] = React.useState<{ category_name?: string }>({});
  const [submitted, setSubmitted] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;

    dispatch(
      partnerEngagementConfigurationSliceAction.resetEngagementCategoryMutationStatus()
    );
    setSubmitted(false);
    setForm({
      ID: initialValue?.ID,
      category_name: initialValue?.category_name ?? "",
    });
    setErrors({});
    // Only re-init when the modal opens or the edited category id changes.
    // Do not depend on a new initialValue object each parent render — that
    // resets submitted mid-update and hides the success/failure view.
  }, [dispatch, open, mode, initialValue?.ID]);

  const isLoading =
    mode === "add"
      ? addEngagementCategoryLoading
      : updateEngagementCategoryLoading;
  const status =
    mode === "add" ? addEngagementCategoryStatus : updateEngagementCategoryStatus;
  const message =
    mode === "add"
      ? addEngagementCategoryMessage
      : updateEngagementCategoryMessage;

  const validate = () => {
    const next: { category_name?: string } = {};
    if (!form.category_name.trim()) {
      next.category_name = "Category name is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitted(true);
    onSubmit({
      ID: form.ID,
      category_name: form.category_name.trim(),
    });
  };

  const title = mode === "add" ? "Add Category" : "Edit Category";
  const subtitle =
    mode === "add"
      ? "Create a new engagement category"
      : "Update engagement category details";
  const ctaLabel = mode === "add" ? "ADD CATEGORY" : "UPDATE";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="engagement-category-modal"
    >
      {(!submitted || isLoading) && (
        <DialogTitle className="dialog-title">
          <Box sx={{ pr: 1 }}>
            <Typography className="title-text">{title}</Typography>
            <Typography className="subtitle-text" variant="body2">
              {subtitle}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ mt: 0.5 }}>
            <Iconography icon="xmark" />
          </IconButton>
        </DialogTitle>
      )}

      {submitted ? (
        <DialogContent className="dialog-result">
          {isLoading && <Loader variant={LoaderSizes.medium} />}
          {!isLoading && (
            <>
              <Iconography
                icon={status ? "circleCheck" : "circleXmark"}
                size={IconographySizes.XLarge}
                theme={
                  status ? IconographyThemes.Success : IconographyThemes.Error
                }
              />
              <Typography variant="body2">
                {message ||
                  (status
                    ? mode === "add"
                      ? "Engagement category added successfully"
                      : "Engagement category updated successfully"
                    : "Failed to save engagement category")}
              </Typography>
              <Button label="Close" onClick={onClose} variant="primary" />
            </>
          )}
        </DialogContent>
      ) : (
        <>
          <DialogContent className="dialog-content">
            <FormControl fullWidth variant="filled">
              <InputLabel>Category Name</InputLabel>
              <TextField
                id="category-name"
                placeholder="Enter category name"
                value={form.category_name}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    category_name: e.target.value,
                  }))
                }
                error={!!errors.category_name}
                helperText={errors.category_name || ""}
              />
            </FormControl>
          </DialogContent>
          <DialogActions className="dialog-actions">
            <Button label="Cancel" variant="secondary" onClick={onClose} />
            <Button label={ctaLabel} variant="primary" onClick={handleSubmit} />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AddEditEngagementCategoryModal;
